import { isTieredVariant } from '@lub0v/santa-utils';
import { useLocation } from 'react-router-dom';
import { usePremiumInfoUrl } from '@src/utils/premiumReturn';
import { isDotCom } from '@src/site';
import { useAppSelector } from '@src/state/hooks';
import { premiumFeatureTier } from '@src/pages/PremiumPage/featureTier';
import { premiumFeatureAction } from '@src/pages/PremiumPage/featureTier';
import { PremiumOfferView } from '@src/components/PremiumUpsell/PremiumOfferView';
import { useStartPremiumPurchase } from './premiumInterest';
import { Link } from '@src/components/Link/Link';
import { usePremiumOffer } from '@src/pages/PremiumPage/PremiumOfferProvider';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@src/components/Button/Button';
import { usePremiumModal, type PremiumFeature } from './PremiumModal';
import { CrownIcon } from '@src/components/icons/CrownIcon';
import { MessageIcon } from '@src/components/icons/MessageIcon';
import { UsersIcon } from '@src/components/icons/UsersIcon';
import { SettingsIcon } from '@src/components/icons/SettingsIcon';
import { RefreshIcon } from '@src/components/icons/RefreshIcon';
import { WrenchIcon } from '@src/components/icons/WrenchIcon';
import { EyeIcon } from '@src/components/icons/EyeIcon';
import { StarIcon } from '@src/components/icons/StarIcon';
import './PremiumUpsell.css';

type Feature = PremiumFeature;
const icons = [<StarIcon />, <MessageIcon />, <UsersIcon />];

export function PremiumUpsell({ feature, children, compact = false, testId, secondaryAction, completedDraw = false, layout = 'stacked', context }: {
  context?: string; feature: Feature; children?: ReactNode; compact?: boolean; testId?: string; secondaryAction?: ReactNode; completedDraw?: boolean; layout?: 'stacked' | 'split';
}) {
  const startPremiumPurchase = useStartPremiumPurchase();
  const { t, i18n } = useTranslation();
  const offer = usePremiumOffer(feature);
  const user = useAppSelector(state => state.session.user);
  const access = user?.premium?.offers.find(item => item.tier === premiumFeatureTier(feature));
  const openPremium = usePremiumModal();
  const creation = useLocation().pathname.replace(/\/$/, '') === '/box/new';
  const premiumInfoHref = usePremiumInfoUrl();
  const copyGame = feature === 'draw' && context === 'copy_game';
  const prefix = `premiumUpsell.${copyGame ? 'copyExclusions' : completedDraw ? 'drawCompleted' : feature}`;
  const benefitIcons = feature === 'themes' ? [<StarIcon />, <MessageIcon />, <WrenchIcon />] : feature === 'draw' ? [<WrenchIcon />, <RefreshIcon />, <SettingsIcon />] : feature === 'history' ? [<UsersIcon />, <SettingsIcon />, <EyeIcon />] : icons;
  const benefits = <ul className="premium-upsell__benefits">{[0, 1, 2].map((index) => (
    <li key={index}><span aria-hidden="true">{benefitIcons[index]}</span><span>{t(feature === 'themes' && index === 1 && !isDotCom() ? 'premiumUpsell.themeAtmosphereNoAds' : `${prefix}.benefits.${index}`)}</span></li>
  ))}</ul>;
  if (isTieredVariant(offer.variant) && access && ['owned', 'included'].includes(access.status)) return null;
  return <aside className={`premium-upsell premium-upsell--${feature}${compact ? ' premium-upsell--compact' : ''}${layout === 'split' ? ' premium-upsell--split' : ''}`} data-premium-tier={isTieredVariant(offer.variant) ? premiumFeatureTier(feature) : undefined} data-testid={testId ?? `premium-upsell-${feature}`}>
    <div className="premium-upsell__intro">
    <div className="premium-upsell__copy">
      <span className="premium-upsell__emblem" aria-hidden="true"><CrownIcon /></span>
      <div><h3>{t(`${prefix}.title`)}</h3><p>{t(`${prefix}.${compact && feature === 'themes' ? 'compactBody' : 'body'}`)}</p>{feature === 'history' && <p className="premium-upsell__history-caption">{t('premiumUpsell.history.caption')}</p>}</div>
    </div>
    {children}
    {layout !== 'split' && compact && !copyGame && (feature === 'history' || feature === 'draw') && benefits}
    </div>
    {(!compact || layout === 'split') && benefits}
    <div className="premium-upsell__actions">
      <Button className="premium-upsell__primary" fullWidth icon={<CrownIcon />} onClick={() => startPremiumPurchase(testId ?? 'upsell', feature, context)}><span className="premium-upsell__button-label"><span><PremiumOfferView placement={testId ?? 'upsell'} feature={feature} context={context}>{isTieredVariant(offer.variant) ? premiumFeatureAction(feature, i18n.language, offer.symbolPrice) : t('premiumModal.unlockPriced', { price: offer.symbolPrice })}</PremiumOfferView></span></span></Button>
      {secondaryAction}
      <Link href={creation ? premiumInfoHref : undefined} target={creation ? '_blank' : undefined}
        premiumContext={{ placement: 'upsell_learn_more', feature, context, action: 'learn_more' }}
        onClick={creation ? undefined : () => openPremium(feature, context, 'upsell_learn_more', 'learn_more')}
        testId="premium-upsell-learn-more">{t('premiumModal.learnMore')}</Link>
    </div>
  </aside>;
}
